<template>
  <div class="current-address-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Current Address Section -->
    <section class="current-address-section">
      <div class="container">
        <div class="address-content">
          <PageTitle title="Current Address" />
          
          <div class="address-form">
            <!-- Street Address - Full Width -->
            <div class="form-group full-width">
              <input
                type="text"
                v-model="streetAddress"
                placeholder="Street Address"
                class="address-input"
                :class="{ 'error': showStreetError }"
                @input="clearStreetError"
              />
              <div v-if="showStreetError" class="error-message">Required</div>
            </div>

            <!-- Zip Code and State - Same Row on Desktop -->
            <div class="form-row">
              <div class="form-group">
                <input
                  type="text"
                  v-model="zipCode"
                  placeholder="Zip Code"
                  class="address-input"
                  :class="{ 'error': showZipError }"
                  @input="clearZipError"
                  maxlength="5"
                />
                <div v-if="showZipError" class="error-message">Required</div>
              </div>

              <div class="form-group">
                <select
                  v-model="state"
                  class="address-select"
                  :class="{ 'error': showStateError }"
                  @change="clearStateError"
                >
                  <option value="">State</option>
                  <option v-for="stateOption in usStates" :key="stateOption.value" :value="stateOption.value">
                    {{ stateOption.label }}
                  </option>
                </select>
                <div v-if="showStateError" class="error-message">Required</div>
              </div>
            </div>

            <!-- City - Full Width -->
            <div class="form-group full-width">
              <input
                type="text"
                v-model="city"
                placeholder="City"
                class="address-input"
                :class="{ 'error': showCityError }"
                @input="clearCityError"
              />
              <div v-if="showCityError" class="error-message">Required</div>
            </div>

            <!-- Continue Button -->
            <div class="continue-section">
              <button 
                class="continue-btn"
                @click="continueToNext"
              >
                CONTINUE
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import PageTitle from '@/components/PageTitle.vue'
import { submitDataAndLog } from '@/utils/localStorage'

export default {
  name: 'CurrentAddressPage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      userQuoteData: null,
      streetAddress: '',
      zipCode: '',
      state: '',
      city: '',
      showStreetError: false,
      showZipError: false,
      showStateError: false,
      showCityError: false,
      usStates: [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' },
        { value: 'DC', label: 'District of Columbia' }
      ]
    }
  },
  mounted() {
    this.loadUserData()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          // 检查是否有add_another_driver选择
          if (this.userQuoteData.add_another_driver === undefined) {
            // 如果没有选择，跳转回another-driver页面
            this.$router.push('/another-driver')
            return
          }
          // 加载之前输入的地址信息（如果有的话）
          this.streetAddress = this.userQuoteData.address || ''
          this.zipCode = this.userQuoteData.zip_code || ''
          this.state = this.userQuoteData.state || ''
          this.city = this.userQuoteData.city || ''
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        // 如果没有数据，跳转回首页
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    clearStreetError() {
      if (this.streetAddress && this.streetAddress.trim().length > 0) {
        this.showStreetError = false
      }
    },
    
    clearZipError() {
      if (this.zipCode && this.zipCode.trim().length > 0) {
        this.showZipError = false
      }
    },
    
    clearStateError() {
      if (this.state && this.state !== '') {
        this.showStateError = false
      }
    },
    
    clearCityError() {
      if (this.city && this.city.trim().length > 0) {
        this.showCityError = false
      }
    },
    
    async continueToNext() {
      console.log('CONTINUE button clicked in Current Address page')
      
      // 验证表单
      let hasErrors = false
      
      // 检查Street Address
      if (!this.streetAddress || this.streetAddress.trim().length === 0) {
        this.showStreetError = true
        hasErrors = true
      } else {
        this.showStreetError = false
      }
      
      // 检查Zip Code
      if (!this.zipCode || this.zipCode.trim().length === 0) {
        this.showZipError = true
        hasErrors = true
      } else {
        this.showZipError = false
      }
      
      // 检查State
      if (!this.state || this.state === '') {
        this.showStateError = true
        hasErrors = true
      } else {
        this.showStateError = false
      }
      
      // 检查City
      if (!this.city || this.city.trim().length === 0) {
        this.showCityError = true
        hasErrors = true
      } else {
        this.showCityError = false
      }
      
      // 如果有错误，不继续
      if (hasErrors) {
        console.log('Address validation failed - showing error messages')
        return
      }
      
      // 更新用户数据 - 存储为独立字段
      this.userQuoteData.address = this.streetAddress.trim()
      this.userQuoteData.zip_code = this.zipCode.trim()
      this.userQuoteData.state = this.state
      this.userQuoteData.city = this.city.trim()
      this.saveUserData()
      
      console.log('Address data saved:', {
        address: this.userQuoteData.address,
        zip_code: this.userQuoteData.zip_code,
        state: this.userQuoteData.state,
        city: this.userQuoteData.city
      })
      
      // 提交数据并记录页面提交事件
      await submitDataAndLog('currentaddress')
      // 跳转到Home Ownership页面
      this.$router.push('/home-ownership')
    },
    
    goBack() {
      this.$router.push('/another-driver')
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.current-address-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}


/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Current Address Section */
.current-address-section {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.address-content {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
}

.address-form {
  margin-top: 2rem;
  text-align: left;
}

/* Form Layout */
.form-group {
  position: relative;
  margin-bottom: 2rem;
}

.form-group.full-width {
  width: 100%;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

/* Input and Select Styles */
.address-input, .address-select {
  width: 100%;
  padding: 1rem 0;
  border: none;
  border-bottom: 2px solid #e9ecef;
  border-radius: 0;
  background: transparent;
  font-size: 1rem;
  color: #333;
  transition: border-color 0.3s ease;
  outline: none;
}

.address-input:focus, .address-select:focus {
  border-bottom-color: #007bff;
}

.address-input.error, .address-select.error {
  border-bottom-color: #dc3545;
}

.address-input::placeholder {
  color: #999;
  font-weight: 400;
}

.address-select option {
  padding: 0.5rem;
  background: #fff;
  color: #333;
}

/* Error Message */
.error-message {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  text-align: left;
}

/* Continue Section */
.continue-section {
  margin-top: 3rem;
  padding-top: 2rem;
  text-align: center;
}

.continue-btn {
  padding: 1rem 3rem;
  border: 2px solid #007bff;
  border-radius: 8px;
  background: #fff;
  color: #007bff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.continue-btn:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .address-content {
    padding: 0 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
    margin-bottom: 0;
  }

  .form-group {
    margin-bottom: 2rem;
  }

  .continue-btn {
    width: 100%;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .address-content {
    padding: 0 0.5rem;
  }

  .address-input, .address-select {
    padding: 0.8rem 0;
    font-size: 0.9rem;
  }

  .continue-btn {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }
}
</style>
